import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showSuccess, showApiError } from "@/lib/toast";
import { addContentSplit } from "@/sideEffects/api";
import { selectActiveFirm, selectSelectedFolderGuid } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { FormSchemaType, SplitContentMetaForm } from "../contentSplit/splitContent/form/SplitContentMetaForm";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { formatToUTC } from "@/lib/dateUtils";

interface Props {
  onSuccess: () => void;
  onOpenChange: (open: boolean) => void;
}

export const CreateSplitContent: React.FC<Props> = ({ onSuccess, onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const selectedFolderGuid = useSelector(selectSelectedFolderGuid);

  const { goToSplitContentDetail } = useAppNavigate();
  const queryClient = useQueryClient();
  const addMutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      addContentSplit({
        firmGuid: String(activeFirm?.firmGuid),
        name: values.name,
        screenSizeId: values.screenSizeId,
        layoutConfig: values.layoutConfig,
        startDate: formatToUTC(values.startDate),
        endDate: formatToUTC(values.endDate),
        folderGuid: selectedFolderGuid,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["getContentGroups"] });
      showSuccess(t("common.addedSuccess"));
      goToSplitContentDetail(data.data[0].contentGuid);
      onSuccess();
    },
    onError: showApiError,
  });

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("splitContent.create")}</SheetTitle>
          <SplitContentMetaForm
            buttonText={t("common.add")}
            onSubmit={(values) => {
              addMutation.mutate(values);
            }}
            isLoading={addMutation.isPending}
          />
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
